/**
 * CSVImporter.jsx
 * ---------------
 * Component for importing CSV files with type detection and preview
 */

import React, { useState } from 'react';
import { Upload, X, FileText, AlertCircle } from 'lucide-react';
import dataManagementService from '../../services/dataManagementService';

const CSVImporter = ({ onImport, onCancel }) => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [fields, setFields] = useState([]);
  const [tableName, setTableName] = useState('');
  const [error, setError] = useState(null);

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setParsing(true);

    try {
      const result = await dataManagementService.parseCSVFile(selectedFile);
      setParsedData(result.data);
      setFields(result.fields);
      
      // Auto-generate table name from filename
      const autoTableName = selectedFile.name
        .replace('.csv', '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_');
      setTableName(autoTableName);
    } catch (err) {
      setError('Failed to parse CSV file: ' + err.message);
    } finally {
      setParsing(false);
    }
  };

  const handleFieldTypeChange = (fieldName, newType) => {
    setFields(fields.map(field => 
      field.name === fieldName ? { ...field, type: newType } : field
    ));
  };

  const handleFieldRequiredToggle = (fieldName) => {
    setFields(fields.map(field => 
      field.name === fieldName ? { ...field, required: !field.required } : field
    ));
  };

  const handleImport = () => {
    if (!tableName.trim()) {
      setError('Please enter a table name');
      return;
    }

    if (fields.length === 0) {
      setError('No fields detected in CSV');
      return;
    }

    if (!file) {
      setError('No file selected');
      return;
    }

    // Pass the file and table name to the parent
    // The backend API will handle parsing and type detection
    onImport({
      tableName: tableName.trim(),
      file: file,
      fields, // For display purposes only
      recordCount: parsedData?.length || 0
    });
  };

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'rating', label: 'Rating (1-5)' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'URL' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'select', label: 'Select (Dropdown)' },
    { value: 'multiselect', label: 'Multi-Select' },
    { value: 'color', label: 'Color' },
    { value: 'file', label: 'File URL' },
    { value: 'image', label: 'Image URL' },
    { value: 'json', label: 'JSON' }
  ];

  return (
    <div className="csv-importer-overlay">
      <div className="csv-importer-modal">
        <div className="csv-importer-header">
          <h2>
            <FileText size={24} />
            Import from CSV
          </h2>
          <button onClick={onCancel} className="btn-close">
            <X size={20} />
          </button>
        </div>

        <div className="csv-importer-content">
          {!file && (
            <div className="csv-upload-zone">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                id="csv-file-input"
                style={{ display: 'none' }}
              />
              <label htmlFor="csv-file-input" className="csv-upload-label">
                <Upload size={48} />
                <h3>Upload CSV File</h3>
                <p>Click to select a CSV file or drag and drop</p>
              </label>
            </div>
          )}

          {parsing && (
            <div className="csv-parsing">
              <div className="loading-spinner"></div>
              <p>Parsing CSV file...</p>
            </div>
          )}

          {error && (
            <div className="csv-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {file && parsedData && (
            <div className="csv-preview">
              <div className="csv-file-info">
                <FileText size={20} />
                <span>{file.name}</span>
                <span className="csv-record-count">
                  {parsedData.length} records
                </span>
                <button
                  onClick={() => {
                    setFile(null);
                    setParsedData(null);
                    setFields([]);
                    setTableName('');
                    setError(null);
                  }}
                  className="btn-change-file"
                >
                  Change File
                </button>
              </div>

              <div className="csv-table-name">
                <label>Table Name:</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  placeholder="Enter table name"
                  className="input-table-name"
                />
              </div>

              <div className="csv-fields-config">
                <h3>Configure Fields ({fields.length})</h3>
                <div className="csv-fields-list">
                  {fields.map((field, index) => (
                    <div key={index} className="csv-field-item">
                      <div className="csv-field-name">{field.name}</div>
                      <div className="csv-field-controls">
                        <select
                          value={field.type}
                          onChange={(e) => handleFieldTypeChange(field.name, e.target.value)}
                          className="csv-field-type-select"
                        >
                          {fieldTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        <label className="csv-field-required">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={() => handleFieldRequiredToggle(field.name)}
                          />
                          Required
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="csv-preview-data">
                <h3>Data Preview (first 5 rows)</h3>
                <div className="csv-preview-table-wrapper">
                  <table className="csv-preview-table">
                    <thead>
                      <tr>
                        {fields.map((field, index) => (
                          <th key={index}>{field.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.slice(0, 5).map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {fields.map((field, colIndex) => (
                            <td key={colIndex}>
                              {String(row[field.name] || '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {file && parsedData && (
          <div className="csv-importer-footer">
            <button onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            <button onClick={handleImport} className="btn-import">
              <Upload size={16} />
              Import {parsedData.length} Records
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImporter;


